import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaBars, FaTimes, FaShieldAlt, FaUserMd, FaSignOutAlt, FaChevronDown, FaDumbbell, FaTags, FaCalculator, FaSyringe, FaExternalLinkAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';
import BrandName from './BrandName';

const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isProgramsOpen, setIsProgramsOpen] = useState(false);
  const [isMobileProgramsOpen, setIsMobileProgramsOpen] = useState(false);
  const profileRef = useRef(null);
  const programsRef = useRef(null);
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
      if (programsRef.current && !programsRef.current.contains(event.target)) {
        setIsProgramsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setIsMobileMenuOpen(false);
    setIsProfileOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const programsSubMenu = [
    { path: '/our-program', label: 'Our Program', icon: FaDumbbell },
    { path: '/pricing', label: 'Pricing', icon: FaTags },
    // { href: 'https://glp1.fitnession.fit', label: 'GLP-1 Program', icon: FaSyringe, isExternal: true },
  ];

  const publicNavLinks = [
    { path: '/blog', label: 'Blog' },
    { path: '/calculators', label: 'Tools', icon: FaCalculator },
    { path: '/free-resources', label: 'Free Guides' },
    { path: '/success-stories', label: 'Success Stories' },
  ];

  const authNavLinks = [
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/blog', label: 'Blog' },
    { path: '/calculators', label: 'Tools', icon: FaCalculator },
    { path: '/free-resources', label: 'Free Guides' },
    { path: '/success-stories', label: 'Success Stories' },
  ];

  const navLinks = isAuthenticated ? authNavLinks : publicNavLinks;

  const isProgramsActive = location.pathname === '/our-program' || location.pathname === '/pricing';
  const isAdmin = user?.role === 'ADMIN';
  const isCoach = user?.role === 'COACH';
  const isDoctor = user?.role === 'DOCTOR';

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link
              to="/"
              className="flex items-center space-x-2 hover:opacity-80 transition-opacity"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <img src={logo} alt="Fitnession" className="h-10 w-auto" />
              <BrandName className="text-2xl" />
            </Link>
          </div>

          {/* Desktop Nav - Center */}
          <div className="hidden md:flex flex-1 items-center justify-center space-x-6">
            {/* Programs Dropdown */}
            <div className="relative" ref={programsRef}>
              <button
                onClick={() => setIsProgramsOpen(!isProgramsOpen)}
                className={`inline-flex items-center font-medium transition-colors ${
                  isProgramsActive
                    ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                Programs
                <FaChevronDown className={`ml-1 text-xs transition-transform ${isProgramsOpen ? 'rotate-180' : ''}`} />
              </button>

              {isProgramsOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                  {programsSubMenu.map((item) =>
                    item.isExternal ? (
                      <a
                        key={item.href}
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsProgramsOpen(false)}
                        className="flex items-center gap-3 px-4 py-2 transition-colors text-gray-700 hover:bg-gray-50"
                      >
                        <item.icon className="text-primary-500" />
                        <span>{item.label}</span>
                        <FaExternalLinkAlt className="ml-auto text-xs text-gray-400" />
                      </a>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsProgramsOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2 transition-colors ${
                          isActive(item.path)
                            ? 'bg-primary-50 text-primary-600'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <item.icon className="text-gray-400" />
                        <span>{item.label}</span>
                      </Link>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Nav Links */}
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop Nav - Right */}
          <div className="hidden md:flex flex-shrink-0 items-center space-x-4">
            {isAdmin && (
              <Link
                to="/admin"
                className={`inline-flex items-center font-medium transition-colors ${
                  isActive('/admin')
                    ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                <FaShieldAlt className="mr-1" />
                Admin
              </Link>
            )}

            {isCoach && (
              <Link
                to="/coach-dashboard"
                className={`inline-flex items-center font-medium transition-colors ${
                  location.pathname.startsWith('/coach-dashboard')
                    ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                <FaUserMd className="mr-1" />
                My Clients
              </Link>
            )}

            {isDoctor && (
              <Link
                to="/doctor-dashboard"
                className={`inline-flex items-center font-medium transition-colors ${
                  location.pathname.startsWith('/doctor-dashboard')
                    ? 'text-primary-600 border-b-2 border-primary-600 pb-1'
                    : 'text-gray-700 hover:text-primary-600'
                }`}
              >
                <FaUserMd className="mr-1" />
                My Patients
              </Link>
            )}

            {isAuthenticated ? (
              <div className="relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center gap-2 p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                    {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                  </div>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <div className="font-semibold text-gray-900">
                        {user?.firstName} {user?.lastName}
                      </div>
                      <div className="text-sm text-gray-500 truncate">{user?.email}</div>
                      {user?.role && user.role !== 'PATIENT' && (
                        <span className="inline-block mt-1 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                          {user.role}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <FaSignOutAlt />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors font-medium shadow-lg"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex-shrink-0 ml-auto">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? (
                <FaTimes className="text-2xl" />
              ) : (
                <FaBars className="text-2xl" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 animate-fadeIn">
            <div className="flex flex-col space-y-4">
              {/* Programs Submenu - Mobile */}
              <div>
                <button
                  onClick={() => setIsMobileProgramsOpen(!isMobileProgramsOpen)}
                  className={`w-full flex items-center justify-between font-medium py-2 px-4 rounded-lg transition-colors ${
                    isProgramsActive
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span>Programs</span>
                  <FaChevronDown className={`text-xs transition-transform ${isMobileProgramsOpen ? 'rotate-180' : ''}`} />
                </button>
                {isMobileProgramsOpen && (
                  <div className="ml-4 mt-2 space-y-2">
                    {programsSubMenu.map((item) =>
                      item.isExternal ? (
                        <a
                          key={item.href}
                          href={item.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setIsMobileProgramsOpen(false);
                          }}
                          className="flex items-center gap-3 py-2 px-4 rounded-lg transition-colors text-gray-600 hover:bg-gray-50"
                        >
                          <item.icon className="text-primary-500" />
                          <span>{item.label}</span>
                          <FaExternalLinkAlt className="ml-auto text-xs text-gray-400" />
                        </a>
                      ) : (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setIsMobileProgramsOpen(false);
                          }}
                          className={`flex items-center gap-3 py-2 px-4 rounded-lg transition-colors ${
                            isActive(item.path)
                              ? 'bg-primary-50 text-primary-600'
                              : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <item.icon className="text-gray-400" />
                          <span>{item.label}</span>
                        </Link>
                      )
                    )}
                  </div>
                )}
              </div>

              {/* Nav Links */}
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`font-medium py-2 px-4 rounded-lg transition-colors ${
                    isActive(link.path)
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`inline-flex items-center font-medium py-2 px-4 rounded-lg transition-colors ${
                    isActive('/admin')
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FaShieldAlt className="mr-2" />
                  Admin
                </Link>
              )}

              {isCoach && (
                <Link
                  to="/coach-dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`inline-flex items-center font-medium py-2 px-4 rounded-lg transition-colors ${
                    location.pathname.startsWith('/coach-dashboard')
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FaUserMd className="mr-2" />
                  My Clients
                </Link>
              )}

              {isDoctor && (
                <Link
                  to="/doctor-dashboard"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`inline-flex items-center font-medium py-2 px-4 rounded-lg transition-colors ${
                    location.pathname.startsWith('/doctor-dashboard')
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FaUserMd className="mr-2" />
                  My Patients
                </Link>
              )}

              {isAuthenticated ? (
                <div className="border-t pt-4 px-4">
                  <div className="font-medium text-gray-900 mb-1">
                    {user?.firstName} {user?.lastName}
                  </div>
                  <div className="text-sm text-gray-600 mb-3">{user?.email}</div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left text-red-600 hover:bg-red-50 font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="text-gray-700 hover:bg-gray-50 font-medium py-2 px-4 rounded-lg transition-colors"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="bg-primary-600 text-white px-6 py-3 rounded-lg hover:bg-primary-700 transition-colors font-medium text-center shadow-lg"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Navbar;
